// src/services/apiWithAuth.js
import axios from 'axios';

let isRefreshing = false;
let failedQueue = [];
let logoutCallback = null;
let refreshLoadingCallback = null;
let refreshPromise = null;

// Function to set logout callback from AuthContext
export const setLogoutCallback = (callback, loadingCallback) => {
  console.log('apiWithAuth: Setting logout callback:', typeof callback);
  console.log('apiWithAuth: Setting loading callback:', typeof loadingCallback);
  logoutCallback = callback;
  refreshLoadingCallback = loadingCallback;
};

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

const apiWithAuth = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiWithAuth.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
apiWithAuth.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // 1. Only check for 401 status and no retry flag
    if (error.response?.status === 401 && !originalRequest._retry) {
      
      let isInvalidTokenError = false;

      // --- START: NEW BLOB CHECKING LOGIC ---
      // Check if the error response data is a Blob
      if (error.response.data instanceof Blob) {
        try {
          // Convert the blob to text to read the JSON error inside it
          const errorText = await error.response.data.text();
          const errorJson = JSON.parse(errorText);

          // Now check for the error message in the parsed JSON
          if (errorJson?.error === "Invalid token") {
            isInvalidTokenError = true;
            console.log('401 "Invalid token" error detected *inside blob*.');
          }
        } catch (e) {
          // Could not parse the blob, so we can't be sure
          console.error("Could not parse 401 error blob", e);
        }
      } else {
        // It's not a blob, so check the JSON data directly (your old logic)
        if (error.response?.data?.error === "Invalid token") {
          isInvalidTokenError = true;
        }
      }
      // --- END: NEW BLOB CHECKING LOGIC ---

      // 2. Proceed with refresh *only if* it was the correct token error
      if (isInvalidTokenError) {
        console.log('401 error confirmed, attempting token refresh...');
        
        if (isRefreshing) {
          console.log('Token refresh already in progress, queuing request...');
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiWithAuth(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        if (refreshLoadingCallback) {
          refreshLoadingCallback(true);
        }

        try {
          const refreshToken = localStorage.getItem('refreshToken');
          if (!refreshToken) {
            throw new Error('No refresh token available');
          }

          console.log('apiWithAuth: Calling refresh token API with refreshToken:', refreshToken ? 'exists' : 'missing');
          
          if (!refreshPromise) {
            refreshPromise = axios.post(`${process.env.REACT_APP_API_BASE_URL}/employee-auth/refresh-token`, {
              refreshToken
            });
          }
          
          const response = await refreshPromise;

          const { token: newToken, refreshToken: newRefreshToken } = response.data;
          localStorage.setItem('token', newToken);
          if (newRefreshToken) {
            localStorage.setItem('refreshToken', newRefreshToken);
          }

          console.log('apiWithAuth: Token refresh successful, retrying original request...');
          
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          
          processQueue(null, newToken);
          
          return apiWithAuth(originalRequest);
        } catch (refreshError) {
          console.error('apiWithAuth: Token refresh failed:', refreshError);
          processQueue(refreshError, null);
          
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('kd_admin_employee_profile');
          
          if (logoutCallback) {
            console.log('apiWithAuth: Using logout callback from AuthContext');
            logoutCallback();
          } else {
            console.log('apiWithAuth: No logout callback available, redirecting to login');
            window.location.href = '/login';
          }
          
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
          refreshPromise = null;
          
          if (refreshLoadingCallback) {
            refreshLoadingCallback(false);
          }
        }
      }
    }

    // If it's not a 401, or not the "Invalid token" error, just reject
    return Promise.reject(error);
  }
);

export default apiWithAuth;